/**
 * 创建WebGPU纹理资源
 * @param device WebGPU设备
 * @param image 图像数据
 * @param lutSize LUT尺寸（默认为64）
 * @param maskData 遮罩图像数据（可选）
 * @returns 纹理资源对象
 */
export function createTextures(device, image, lutSize = 64, maskData) {
    // 创建输入纹理
    const inputTexture = device.createTexture({
        size: {
            width: image.width,
            height: image.height,
            depthOrArrayLayers: 1
        },
        format: 'rgba8unorm',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST
    });
    // 创建输出纹理
    const outputTexture = device.createTexture({
        size: {
            width: image.width,
            height: image.height,
            depthOrArrayLayers: 1
        },
        format: 'rgba8unorm',
        usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.COPY_SRC
    });
    // 创建LUT纹理（3D纹理，动态尺寸）
    const lutTexture = device.createTexture({
        size: {
            width: lutSize,
            height: lutSize,
            depthOrArrayLayers: lutSize
        },
        dimension: '3d',
        format: 'rgba8unorm',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST
    });
    // 创建纹理视图
    const inputTextureView = inputTexture.createView();
    const outputTextureView = outputTexture.createView();
    const lutTextureView = lutTexture.createView({
        dimension: '3d'
    });
    // 基础资源对象
    const resources = {
        inputTexture,
        outputTexture,
        lutTexture,
        inputTextureView,
        outputTextureView,
        lutTextureView
    };
    // 如果提供了遮罩数据，创建遮罩纹理
    if (maskData) {
        // 遮罩纹理应该始终使用完整的原始遮罩尺寸
        // 这样着色器中的坐标映射才能正确工作
        const maskTexture = device.createTexture({
            size: {
                width: maskData.width, // 使用原始遮罩宽度
                height: maskData.height, // 使用原始遮罩高度
                depthOrArrayLayers: 1
            },
            format: 'rgba8unorm',
            usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST
        });
        const maskTextureView = maskTexture.createView();
        resources.maskTexture = maskTexture;
        resources.maskTextureView = maskTextureView;
    }
    return resources;
}
/**
 * 销毁WebGPU资源
 * @param resources 纹理资源对象
 */
export function destroyResources(resources) {
    resources.inputTexture.destroy();
    resources.outputTexture.destroy();
    resources.lutTexture.destroy();
    // 清理遮罩纹理（如果存在）
    if (resources.maskTexture) {
        resources.maskTexture.destroy();
    }
}
/**
 * 创建统一缓冲区
 * @param device WebGPU设备
 * @param data 缓冲区数据
 * @returns 统一缓冲区
 */
export function createUniformBuffer(device, data) {
    const buffer = device.createBuffer({
        size: data.byteLength,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });
    device.queue.writeBuffer(buffer, 0, data);
    return buffer;
}
/**
 * 创建采样器
 * @param device WebGPU设备
 * @returns 采样器
 */
export function createSampler(device) {
    return device.createSampler({
        addressModeU: 'clamp-to-edge',
        addressModeV: 'clamp-to-edge',
        addressModeW: 'clamp-to-edge',
        magFilter: 'linear',
        minFilter: 'linear',
        mipmapFilter: 'linear'
    });
}
//# sourceMappingURL=resources.js.map